import clsx from 'clsx';
import { ArrowsOut, Trash } from '@phosphor-icons/react';
import type { Scene, SceneVideo } from '@/types';
import { useMediaUrl, downloadFilename } from '@/services/media';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';

interface Props {
  scene: Scene;
  episodeUuid: string;
  onExtend: (video: SceneVideo) => void;
  onDelete: (videoId: string) => void;
}

export function VideoVariants({ scene, episodeUuid, onExtend, onDelete }: Props) {
  const videos = useEpisodeStore((s) => s.videos);
  const activateScene = useSelectionStore((s) => s.activateScene);
  const setAssetMode = useSelectionStore((s) => s.setAssetMode);

  const variants = scene.videoIds
    .map((id) => videos[id])
    .filter((x): x is SceneVideo => Boolean(x) && !x.deletedAt)
    .sort((a, b) => a.createdAt - b.createdAt);

  if (variants.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-line-2 bg-surface-2 p-6 text-center text-[13px] text-text-3">
        No videos yet. Switch the input to Video mode below and describe motion.
      </p>
    );
  }

  // Group by source image so chains read naturally
  const groups = new Map<string, SceneVideo[]>();
  for (const v of variants) {
    const list = groups.get(v.sourceImageId) ?? [];
    list.push(v);
    groups.set(v.sourceImageId, list);
  }

  function reload(video: SceneVideo) {
    setAssetMode('video');
    activateScene(scene, {
      prompt: video.prompt,
      references: video.references,
      fresh: video.fresh,
    });
  }

  return (
    <div className="space-y-5">
      {Array.from(groups.entries()).map(([sourceImageId, list], gi) => (
        <div key={sourceImageId}>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
            Source {gi + 1}
          </p>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
          >
            {list.map((v, idx) => (
              <VideoCard
                key={v.id}
                video={v}
                scene={scene}
                episodeUuid={episodeUuid}
                index={idx}
                onReload={reload}
                onExtend={onExtend}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoCard({
  video,
  scene,
  episodeUuid,
  index,
  onReload,
  onExtend,
  onDelete,
}: {
  video: SceneVideo;
  scene: Scene;
  episodeUuid: string;
  index: number;
  onReload: (v: SceneVideo) => void;
  onExtend: (v: SceneVideo) => void;
  onDelete: (id: string) => void;
}) {
  const url = useMediaUrl(episodeUuid, video.path);
  const isReady = video.status === 'ready';

  return (
    <div className="overflow-hidden rounded-md bg-surface-inset">
      <button
        type="button"
        onClick={() => onReload(video)}
        className={clsx(
          'relative block aspect-[16/9] w-full',
          isReady ? 'cursor-pointer' : 'cursor-wait',
        )}
        aria-label={`Re-run from video v${index + 1}`}
      >
        {isReady ? (
          <video
            src={url}
            muted
            loop
            playsInline
            className="h-full w-full object-cover"
            onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
          />
        ) : video.status === 'generating' ? (
          <div className="flex h-full w-full items-center justify-center font-mono text-[11px] text-accent">
            generating…
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-[11px] text-err">
            failed
          </div>
        )}
        <span className="absolute left-2 top-2 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-text-1 backdrop-blur-md">
          v{index + 1} · {video.duration}s
        </span>
      </button>
      <div className="flex items-center justify-between border-t border-line-1 bg-surface-2 px-3 py-2">
        <span className="font-mono text-[10px] text-text-3">{video.model}</span>
        <div className="flex gap-3 text-[12px] font-medium">
          <button
            type="button"
            onClick={() => onExtend(video)}
            disabled={!isReady}
            className="inline-flex items-center gap-1 text-text-2 transition-colors hover:text-text-1 disabled:cursor-not-allowed disabled:text-text-4"
          >
            <ArrowsOut size={12} /> Extend
          </button>
          <a
            href={url}
            download={downloadFilename(scene.episodeUuid, scene, video, index)}
            className="text-text-2 transition-colors hover:text-text-1"
          >
            Download
          </a>
          <button
            type="button"
            onClick={() => {
              if (confirm('Delete this video? You can recover it within 30 days.')) {
                onDelete(video.id);
              }
            }}
            className="inline-flex items-center gap-1 text-text-3 transition-colors hover:text-err"
          >
            <Trash size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
